import { Box, Button, CircularProgress, TableCell, TableRow, Typography, useTheme } from "@mui/material";
import { SecurityExclusionAuditDataObj } from "../../../../redux-store/types/onBoarding";
import { PaginationObj, TableHeaderData } from "../../../investorOverview/types";
import CustomTable from "../../../investorOverview/common/customTable";
import dayjs from "../../../../utils/dayjs";

const headerData: TableHeaderData[] = [
    {
        id: 'investorName',
        label: 'Name',
        align: 'left',
        isSortable: false,
    },
    {
        id: 'securityName',
        label: 'Securities',
        align: 'center',
        isSortable: false,
    },
    {
        id: 'modifiedDate',
        label: 'Date of Change',
        align: 'center',
        isSortable: false,
    },
    {
        id: 'isExcluded',
        label: 'Is Excluded?',
        align: 'center',
        isSortable: false,
    },
    {
        id: 'exclusionReason',
        label: 'Exclusion Reason',
        align: 'center',
        isSortable: false,
    },
    {
        id: 'auditDetails',
        label: '',
        align: 'center',
        isSortable: false,
    },
]

interface DataTableProps {
    tableData: Array<SecurityExclusionAuditDataObj>;
    handleViewDetailsClick: (id: number) => void;
    loadingRowId: number | null;
}

function DataTable({
    tableData,
    handleViewDetailsClick,
    loadingRowId
}: DataTableProps): JSX.Element {
    const theme = useTheme()

    return (
        <>
            {tableData.map((rowData: SecurityExclusionAuditDataObj, index: number) => (
                <TableRow
                    key={`${rowData.id}-${index}`}
                    sx={{
                        '& td': {
                            paddingTop: "15px",
                            paddingBottom: "15px",
                            borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                        },
                        '& td:first-of-type': {
                            paddingLeft: "15px",
                        }
                    }}
                >
                    <TableCell align="left">
                        <Typography
                            variant="body_500"
                            sx={{
                                lineHeight: "20px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.primary,
                            }}
                        >
                            {rowData?.investorName ?? "-"} {rowData?.clientCode ? "(" + (rowData?.clientCode) + ")" : ""}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={{
                                lineHeight: "20px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.primary,
                            }}
                        >
                            {rowData?.securityName ?? "-"} {rowData?.security ? "(" + (rowData?.security) + ")" : ""}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={{
                                lineHeight: "20px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.primary,
                            }}
                        >
                            {rowData?.modifiedDate ? dayjs(rowData.modifiedDate).format('DD/MM/YYYY') : "-"}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={{
                                lineHeight: "20px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.primary,
                            }}
                        >
                            {["y"].includes(`${rowData?.isExcluded ?? ""}`.toLowerCase()) ? "Yes" : "No"}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={{
                                lineHeight: "20px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.primary,
                            }}
                        >
                            {["y"].includes(`${rowData?.isExcluded ?? ""}`.toLowerCase()) ? (rowData?.exclusionReason ?? "-") : "-"}
                        </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ width: "150px", minWidth: "150px" }}>
                        {loadingRowId === rowData.id ? (
                            <Box sx={{ padding: "8px" }}>
                                <CircularProgress size={20} />
                            </Box>
                        ) : (
                            <Button
                                onClick={() => handleViewDetailsClick(rowData.id)}
                                disabled={loadingRowId !== null}
                                sx={{
                                    color: theme.palette.primary[500],
                                    padding: "5px 8px",
                                    ...theme.typography.body_500
                                }}
                            >
                                View Details
                            </Button>
                        )}
                    </TableCell>
                </TableRow>
            ))}
        </>
    )
}

interface AuditHistoryTableProps {
    isLoading: boolean;
    tableData: Array<SecurityExclusionAuditDataObj>
    tablePaginationObj: PaginationObj;
    handleTableRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleTablePageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
    handleViewDetailsClick: (id: number) => void;
    loadingRowId: number | null;
}

export default function AuditHistoryTable({
    isLoading,
    tableData,
    tablePaginationObj,
    handleTableRowsPerPageChange,
    handleTablePageChange,
    handleViewDetailsClick,
    loadingRowId
}: AuditHistoryTableProps): JSX.Element {
    return (
        <CustomTable
            isLoading={isLoading}
            tableBody={
                <DataTable
                    tableData={tableData}
                    handleViewDetailsClick={handleViewDetailsClick}
                    loadingRowId={loadingRowId}
                />
            }
            tableData={tableData}
            tableHeaderData={headerData}
            tableHeadRowCustomStyles={{
                '& th:first-of-type': {
                    paddingLeft: "20px"
                }
            }}
            tableBodyCustomStyles={{
                '& tr:last-child td': {
                    border: 0
                }
            }}
            loadingRowsNum={7}
            tableDataCount={tablePaginationObj.totalRows}
            rowsPerPage={tablePaginationObj.rowsPerPage}
            rowsPerPageOptions={[5, 10, 15, 20]}
            handleRowsPerPageChange={handleTableRowsPerPageChange}
            handlePageChange={handleTablePageChange}
            currentPage={tablePaginationObj.currentPage}
        />
    )
}